import { useState, useEffect, useCallback } from "react";

// ============================================
// MISE ADMIN — PAINEL NEUROTECH-X
// Gestão de Restaurantes
// ============================================

const API = window.location.origin;

const C = {
  bg: "#0B0F1A", surface: "#111827", surfaceAlt: "#1A2236", card: "#161E2E",
  border: "#2A3348", borderLight: "#1E293B", text: "#F1F5F9", textMid: "#94A3B8",
  textMuted: "#64748B", accent: "#E8593C", accentSoft: "rgba(232,89,60,0.12)",
  green: "#22C55E", greenSoft: "rgba(34,197,94,0.12)", gold: "#EAB308",
  goldSoft: "rgba(234,179,8,0.12)", blue: "#3B82F6", blueSoft: "rgba(59,130,246,0.12)",
  red: "#EF4444", redSoft: "rgba(239,68,68,0.12)",
};

const STATUS_COLORS = {
  active: { label: "Ativo", color: C.green, bg: C.greenSoft },
  trial: { label: "Trial", color: C.gold, bg: C.goldSoft },
  suspended: { label: "Suspenso", color: C.red, bg: C.redSoft },
  cancelled: { label: "Cancelado", color: C.textMuted, bg: "rgba(100,116,139,0.12)" },
};

const adminApi = {
  getToken: () => sessionStorage.getItem("mise_admin_token"),
  headers: () => ({ "Content-Type": "application/json", Authorization: `Bearer ${sessionStorage.getItem("mise_admin_token")}` }),
  async fetch(path, opts = {}) {
    const res = await fetch(`${API}${path}`, { ...opts, headers: { ...adminApi.headers(), ...opts.headers } });
    if (res.status === 401) { sessionStorage.removeItem("mise_admin_token"); window.location.reload(); return null; }
    return res.json();
  },
  get: (p) => adminApi.fetch(p),
  post: (p, b) => adminApi.fetch(p, { method: "POST", body: JSON.stringify(b) }),
  put: (p, b) => adminApi.fetch(p, { method: "PUT", body: JSON.stringify(b) }),
  patch: (p, b) => adminApi.fetch(p, { method: "PATCH", body: JSON.stringify(b) }),
  del: (p) => adminApi.fetch(p, { method: "DELETE" }),
};

// ============================================
// ADMIN LOGIN
// ============================================
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) { setError("Preencha todos os campos"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Erro"); setLoading(false); return; }
      sessionStorage.setItem("mise_admin_token", data.token);
      onLogin(data);
    } catch { setError("Erro de conexão"); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ background: C.surface, borderRadius: 16, padding: "40px 36px", width: 380, border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: 8, color: C.text, fontFamily: "'Playfair Display', serif" }}>MISE</div>
          <div style={{ fontSize: 10, letterSpacing: 3, color: C.accent, marginTop: 4 }}>ADMIN · NEUROTECH-X</div>
        </div>
        {error && <div style={{ background: C.redSoft, color: C.red, padding: "8px 12px", borderRadius: 8, fontSize: 13, marginBottom: 14, textAlign: "center" }}>{error}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="E-mail admin"
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            style={{ width: "100%", padding: "11px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, fontFamily: "'DM Sans',sans-serif", outline: "none", boxSizing: "border-box" }} />
          <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Senha"
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            style={{ width: "100%", padding: "11px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 14, fontFamily: "'DM Sans',sans-serif", outline: "none", boxSizing: "border-box" }} />
          <button onClick={handleLogin} disabled={loading} style={{
            padding: "12px 0", borderRadius: 8, border: "none", background: C.accent, color: "#fff",
            fontSize: 14, fontWeight: 600, cursor: loading ? "wait" : "pointer", marginTop: 4, opacity: loading ? 0.7 : 1,
          }}>{loading ? "Entrando..." : "Entrar"}</button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// DASHBOARD VIEW
// ============================================
const DashboardView = ({ data }) => {
  if (!data) return <div style={{ color: C.textMuted, padding: 40 }}>Carregando...</div>;
  const { tenants, today, expiring_soon } = data;
  const cards = [
    { label: "Total Restaurantes", value: tenants.total_tenants, color: C.blue, bg: C.blueSoft },
    { label: "Ativos", value: tenants.active, color: C.green, bg: C.greenSoft },
    { label: "Trial", value: tenants.trial, color: C.gold, bg: C.goldSoft },
    { label: "Suspensos", value: tenants.suspended, color: C.red, bg: C.redSoft },
    { label: "Pedidos Hoje", value: today.total, color: C.accent, bg: C.accentSoft },
    { label: "Faturamento Hoje", value: `R$ ${Number(today.revenue).toFixed(2)}`, color: C.green, bg: C.greenSoft },
    { label: "Vencendo em 7 dias", value: expiring_soon, color: C.gold, bg: C.goldSoft },
    { label: "Cancelados", value: tenants.cancelled, color: C.textMuted, bg: "rgba(100,116,139,0.08)" },
  ];
  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: "0 0 24px", fontFamily: "'Playfair Display', serif" }}>Dashboard</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
        {cards.map((c, i) => (
          <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px" }}>
            <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{c.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: c.color, fontFamily: "'Playfair Display', serif" }}>{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// TENANT FORM (criar/editar)
// ============================================
const TenantForm = ({ tenant, onSave, onCancel }) => {
  const isEdit = !!tenant;
  const [form, setForm] = useState({
    name: tenant?.name || "", slug: tenant?.slug || "", phone: tenant?.phone || "",
    whatsapp: tenant?.whatsapp || "", address: tenant?.address || "", city: tenant?.city || "",
    plan: tenant?.plan || "starter", plan_expires_at: tenant?.plan_expires_at ? tenant.plan_expires_at.substring(0, 10) : "",
    delivery_fee: tenant?.delivery_fee || "0", min_order_value: tenant?.min_order_value || "0",
    estimated_delivery_min: tenant?.estimated_delivery_min || "40",
    owner_name: "", owner_email: "", owner_password: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleSave = async () => {
    if (!form.name || !form.slug) { setError("Nome e slug obrigatórios"); return; }
    if (!isEdit && (!form.owner_name || !form.owner_email || !form.owner_password)) {
      setError("Dados do proprietário obrigatórios"); return;
    }
    setSaving(true); setError("");
    const body = { ...form, delivery_fee: parseFloat(form.delivery_fee), min_order_value: parseFloat(form.min_order_value), estimated_delivery_min: parseInt(form.estimated_delivery_min) };
    if (!body.plan_expires_at) delete body.plan_expires_at;
    const result = isEdit
      ? await adminApi.put(`/api/admin/tenants/${tenant.id}`, body)
      : await adminApi.post("/api/admin/tenants", body);
    setSaving(false);
    if (result?.error) { setError(result.error); return; }
    onSave();
  };

  const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 13, fontFamily: "'DM Sans',sans-serif", outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: 11, color: C.textMid, marginBottom: 4, display: "block", textTransform: "uppercase", letterSpacing: 0.5 };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.accent}`, borderRadius: 14, padding: 24, marginBottom: 20 }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 20 }}>{isEdit ? "Editar Restaurante" : "Novo Restaurante"}</div>
      {error && <div style={{ background: C.redSoft, color: C.red, padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 14 }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div><label style={labelStyle}>Nome do restaurante</label><input value={form.name} onChange={e => set("name", e.target.value)} style={inputStyle} /></div>
        <div><label style={labelStyle}>Slug (URL)</label><input value={form.slug} onChange={e => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} style={inputStyle} disabled={isEdit} /></div>
        <div><label style={labelStyle}>Cidade</label><input value={form.city} onChange={e => set("city", e.target.value)} style={inputStyle} /></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div><label style={labelStyle}>Telefone</label><input value={form.phone} onChange={e => set("phone", e.target.value)} style={inputStyle} /></div>
        <div><label style={labelStyle}>WhatsApp</label><input value={form.whatsapp} onChange={e => set("whatsapp", e.target.value)} style={inputStyle} /></div>
        <div><label style={labelStyle}>Endereço</label><input value={form.address} onChange={e => set("address", e.target.value)} style={inputStyle} /></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Plano</label>
          <select value={form.plan} onChange={e => set("plan", e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
            <option value="starter">Starter</option>
            <option value="essencial">Essencial</option>
            <option value="pro">Pro</option>
          </select>
        </div>
        <div><label style={labelStyle}>Vencimento</label><input type="date" value={form.plan_expires_at} onChange={e => set("plan_expires_at", e.target.value)} style={inputStyle} /></div>
        <div><label style={labelStyle}>Taxa entrega (R$)</label><input type="number" value={form.delivery_fee} onChange={e => set("delivery_fee", e.target.value)} style={inputStyle} /></div>
        <div><label style={labelStyle}>Tempo entrega (min)</label><input type="number" value={form.estimated_delivery_min} onChange={e => set("estimated_delivery_min", e.target.value)} style={inputStyle} /></div>
      </div>

      {!isEdit && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.accent, marginBottom: 12, marginTop: 8 }}>Proprietário (Owner)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div><label style={labelStyle}>Nome</label><input value={form.owner_name} onChange={e => set("owner_name", e.target.value)} style={inputStyle} /></div>
            <div><label style={labelStyle}>E-mail</label><input value={form.owner_email} onChange={e => set("owner_email", e.target.value)} type="email" style={inputStyle} /></div>
            <div><label style={labelStyle}>Senha</label><input value={form.owner_password} onChange={e => set("owner_password", e.target.value)} type="password" style={inputStyle} /></div>
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
        <button onClick={onCancel} style={{ padding: "9px 20px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.textMid, fontSize: 13, cursor: "pointer" }}>Cancelar</button>
        <button onClick={handleSave} disabled={saving} style={{ padding: "9px 24px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>{saving ? "Salvando..." : "Salvar"}</button>
      </div>
    </div>
  );
};

// ============================================
// TENANTS LIST VIEW
// ============================================
const TenantsView = ({ tenants, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [actionLoading, setActionLoading] = useState(null);
  const [showRenew, setShowRenew] = useState(null);
  const [renewDate, setRenewDate] = useState("");
  const [showUsers, setShowUsers] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [resetPwId, setResetPwId] = useState(null);
  const [resetPwVal, setResetPwVal] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const loadTenantUsers = async (tenantId) => {
    const data = await adminApi.get("/api/admin/tenants/" + tenantId);
    if (data && data.users) { setTenantUsers(data.users); setShowUsers(tenantId); }
  };
  const handleAdminResetPw = async () => {
    if (!resetPwVal || resetPwVal.length < 4) { setResetMsg("Minimo 4 caracteres"); return; }
    const res = await adminApi.patch("/api/admin/users/" + resetPwId + "/reset-password", { password: resetPwVal });
    if (res && res.success) { setResetMsg("Senha alterada!"); setResetPwVal(""); setResetPwId(null); setTimeout(() => setResetMsg(""), 2000); }
    else { setResetMsg(res?.error || "Erro"); }
  };

  const filtered = tenants.filter(t => {
    const matchSearch = t.name.toLowerCase().includes(search.toLowerCase()) || t.slug.includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || t.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const changeStatus = async (id, status) => {
    if (!window.confirm(`Confirma ${status === "cancelled" ? "CANCELAR" : status === "suspended" ? "SUSPENDER" : "ATIVAR"} este restaurante?`)) return;
    setActionLoading(id);
    await adminApi.patch(`/api/admin/tenants/${id}/status`, { status });
    await onRefresh();
    setActionLoading(null);
  };

  const deleteTenant = async (id, name) => {
    if (!window.confirm(`ATENÇÃO: Apagar "${name}" PERMANENTEMENTE? Todos os dados serão excluídos.`)) return;
    if (!window.confirm(`Tem certeza? Esta ação NÃO pode ser desfeita!`)) return;
    setActionLoading(id);
    await adminApi.del(`/api/admin/tenants/${id}`);
    await onRefresh();
    setActionLoading(null);
  };

  const renewTenant = async (id) => {
    if (!renewDate) return;
    setActionLoading(id);
    await adminApi.patch(`/api/admin/tenants/${id}/renew`, { plan_expires_at: renewDate });
    await onRefresh();
    setShowRenew(null); setRenewDate(""); setActionLoading(null);
  };

  const isExpired = (date) => date && new Date(date) < new Date();
  const isExpiringSoon = (date) => date && new Date(date) < new Date(Date.now() + 7 * 86400000) && !isExpired(date);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0, fontFamily: "'Playfair Display', serif" }}>Restaurantes</h2>
        <button onClick={() => { setShowForm(true); setEditTenant(null); }} style={{
          padding: "10px 20px", borderRadius: 8, border: "none", background: C.accent, color: "#fff",
          fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
        }}>+ Novo Restaurante</button>
      </div>

      {(showForm || editTenant) && (
        <TenantForm tenant={editTenant} onSave={() => { setShowForm(false); setEditTenant(null); onRefresh(); }} onCancel={() => { setShowForm(false); setEditTenant(null); }} />
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nome ou slug..."
          style={{ padding: "9px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 13, width: 260, fontFamily: "'DM Sans',sans-serif", outline: "none" }} />
        {["all", "active", "trial", "suspended", "cancelled"].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{
            padding: "7px 14px", borderRadius: 8, border: `1px solid ${filterStatus === s ? C.accent : C.border}`,
            background: filterStatus === s ? C.accentSoft : "transparent", color: filterStatus === s ? C.accent : C.textMid,
            fontSize: 12, fontWeight: 500, cursor: "pointer",
          }}>{s === "all" ? "Todos" : STATUS_COLORS[s]?.label || s}</button>
        ))}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: C.textMuted }}>{filtered.length} restaurante{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Table */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 100px 120px 80px 80px 200px", padding: "12px 20px", borderBottom: `1px solid ${C.border}`, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.8 }}>
          <span>Restaurante</span><span>Plano</span><span>Status</span><span>Vencimento</span><span>Pedidos</span><span>Users</span><span style={{ textAlign: "right" }}>Ações</span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: C.textMuted, fontSize: 13 }}>Nenhum restaurante encontrado</div>
        ) : filtered.map(t => {
          const st = STATUS_COLORS[t.status] || STATUS_COLORS.active;
          const expired = isExpired(t.plan_expires_at);
          const expiring = isExpiringSoon(t.plan_expires_at);
          return (
            <div key={t.id} style={{ display: "grid", gridTemplateColumns: "1fr 100px 100px 120px 80px 80px 200px", padding: "14px 20px", borderBottom: `1px solid ${C.borderLight}`, alignItems: "center", fontSize: 13, opacity: t.status === "cancelled" ? 0.5 : 1 }}>
              <div>
                <div style={{ fontWeight: 600, color: C.text }}>{t.name}</div>
                <div style={{ fontSize: 11, color: C.textMuted }}>{t.slug} · {t.city || "—"}</div>
              </div>
              <span style={{ fontSize: 12, color: C.textMid, textTransform: "capitalize" }}>{t.plan}</span>
              <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 12, background: st.bg, color: st.color, fontWeight: 600, display: "inline-block", width: "fit-content" }}>{st.label}</span>
              <div>
                {t.plan_expires_at ? (
                  <span style={{ fontSize: 12, color: expired ? C.red : expiring ? C.gold : C.textMid, fontWeight: expired || expiring ? 600 : 400 }}>
                    {expired ? "⚠️ " : expiring ? "⏳ " : ""}{new Date(t.plan_expires_at).toLocaleDateString("pt-BR")}
                  </span>
                ) : <span style={{ fontSize: 12, color: C.textMuted }}>—</span>}
              </div>
              <span style={{ color: C.textMid, fontSize: 13 }}>{t.total_orders || 0}</span>
              <span style={{ color: C.textMid, fontSize: 13 }}>{t.total_users || 0}</span>
              <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                {showRenew === t.id ? (
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <input type="date" value={renewDate} onChange={e => setRenewDate(e.target.value)}
                      style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 12 }} />
                    <button onClick={() => renewTenant(t.id)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: C.green, color: "#fff", fontSize: 11, cursor: "pointer" }}>✓</button>
                    <button onClick={() => { setShowRenew(null); setRenewDate(""); }} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.textMid, fontSize: 11, cursor: "pointer" }}>✕</button>
                  </div>
                ) : (
                  <>
                    <button onClick={() => setShowRenew(t.id)} title="Renovar" style={{ padding: "5px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.gold, fontSize: 11, cursor: "pointer" }}>📅</button>
                    <button onClick={() => loadTenantUsers(t.id)} title="Usuarios/Senhas" style={{ padding: "5px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.green, fontSize: 11, cursor: "pointer" }}>🔑</button>
                    <button onClick={() => setEditTenant(t)} title="Editar" style={{ padding: "5px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.blue, fontSize: 11, cursor: "pointer" }}>✏️</button>
                    {t.status === "active" ? (
                      <button onClick={() => changeStatus(t.id, "suspended")} title="Suspender" disabled={actionLoading === t.id} style={{ padding: "5px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.gold, fontSize: 11, cursor: "pointer" }}>⏸️</button>
                    ) : t.status !== "cancelled" ? (
                      <button onClick={() => changeStatus(t.id, "active")} title="Ativar" disabled={actionLoading === t.id} style={{ padding: "5px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.green, fontSize: 11, cursor: "pointer" }}>▶️</button>
                    ) : null}
                    <button onClick={() => changeStatus(t.id, "cancelled")} title="Cancelar" disabled={actionLoading === t.id || t.status === "cancelled"} style={{ padding: "5px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.red, fontSize: 11, cursor: "pointer", opacity: t.status === "cancelled" ? 0.3 : 1 }}>🚫</button>
                    <button onClick={() => deleteTenant(t.id, t.name)} title="Apagar total" disabled={actionLoading === t.id} style={{ padding: "5px 8px", borderRadius: 6, border: `1px solid ${C.red}40`, background: C.redSoft, color: C.red, fontSize: 11, cursor: "pointer" }}>🗑️</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {showUsers && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={() => { setShowUsers(null); setResetPwId(null); setResetMsg(""); }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 12, padding: 28, width: 500, maxHeight: "70vh", overflow: "auto", border: "1px solid " + C.border }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 16, fontFamily: "'Playfair Display',serif" }}>Usuarios do Restaurante</h3>
            {resetMsg && <div style={{ padding: "8px 12px", borderRadius: 8, background: resetMsg.includes("alterada") ? C.greenSoft : C.redSoft, color: resetMsg.includes("alterada") ? C.green : C.red, fontSize: 13, marginBottom: 12 }}>{resetMsg}</div>}
            {tenantUsers.length === 0 ? (
              <div style={{ color: C.textMuted, fontSize: 13, padding: 20, textAlign: "center" }}>Nenhum usuario</div>
            ) : tenantUsers.map(u => (
              <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid " + C.borderLight }}>
                <div>
                  <div style={{ fontWeight: 600, color: C.text, fontSize: 14 }}>{u.name}</div>
                  <div style={{ fontSize: 12, color: C.textMuted }}>{u.email} · {u.role}</div>
                </div>
                {resetPwId === u.id ? (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input type="password" placeholder="Nova senha" value={resetPwVal} onChange={e => setResetPwVal(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAdminResetPw()} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid " + C.border, background: C.bg, color: C.text, fontSize: 12, width: 140, outline: "none" }} />
                    <button onClick={handleAdminResetPw} style={{ padding: "5px 10px", borderRadius: 6, border: "none", background: C.green, color: "#fff", fontSize: 11, cursor: "pointer" }}>OK</button>
                    <button onClick={() => { setResetPwId(null); setResetPwVal(""); }} style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid " + C.border, background: "transparent", color: C.textMid, fontSize: 11, cursor: "pointer" }}>X</button>
                  </div>
                ) : (
                  <button onClick={() => { setResetPwId(u.id); setResetPwVal(""); setResetMsg(""); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid " + C.border, background: "transparent", color: C.gold, fontSize: 12, cursor: "pointer" }}>🔑 Resetar Senha</button>
                )}
              </div>
            ))}
            <div style={{ textAlign: "right", marginTop: 16 }}>
              <button onClick={() => { setShowUsers(null); setResetPwId(null); setResetMsg(""); }} style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid " + C.border, background: "transparent", color: C.text, fontSize: 13, cursor: "pointer" }}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN ADMIN APP
// ============================================
export default function MiseAdmin() {
  const [auth, setAuth] = useState(!!sessionStorage.getItem("mise_admin_token"));
  const [tab, setTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [tenants, setTenants] = useState([]);

  const loadDashboard = useCallback(async () => {
    const d = await adminApi.get("/api/admin/dashboard");
    if (d) setDashboard(d);
  }, []);

  const loadTenants = useCallback(async () => {
    const t = await adminApi.get("/api/admin/tenants");
    if (t) setTenants(t);
  }, []);

  useEffect(() => {
    if (auth) { loadDashboard(); loadTenants(); }
  }, [auth, loadDashboard, loadTenants]);

  if (!auth) return <AdminLogin onLogin={() => setAuth(true)} />;

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: "📊" },
    { id: "tenants", label: "Restaurantes", icon: "🍽️" },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", background: C.bg, fontFamily: "'DM Sans', sans-serif", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }
        button { font-family: 'DM Sans', sans-serif; transition: all 0.15s; } button:hover { filter: brightness(1.1); }
        input:focus, select:focus { border-color: ${C.accent} !important; outline: none; }
      `}</style>

      {/* Sidebar */}
      <div style={{ width: 220, background: C.surface, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "24px 20px", borderBottom: `1px solid ${C.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: 8, color: C.text, fontFamily: "'Playfair Display', serif" }}>MISE</div>
          <div style={{ fontSize: 9, letterSpacing: 3, color: C.accent, marginTop: 2 }}>ADMIN PANEL</div>
        </div>
        <div style={{ padding: "14px 10px", flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
          {tabs.map(t => (
            <div key={t.id} onClick={() => setTab(t.id)} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 8,
              cursor: "pointer", background: tab === t.id ? C.accentSoft : "transparent",
              color: tab === t.id ? C.accent : C.textMid, fontWeight: tab === t.id ? 600 : 400, fontSize: 13,
            }}>
              <span style={{ fontSize: 16 }}>{t.icon}</span>{t.label}
            </div>
          ))}
        </div>
        <div style={{ padding: "12px" }}>
          <div onClick={() => { sessionStorage.removeItem("mise_admin_token"); setAuth(false); }} style={{
            display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 8,
            cursor: "pointer", color: C.red, fontSize: 13,
          }}>🚪 Sair</div>
        </div>
        <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.textMuted }}>
          Neurotech-X Admin
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: "auto", padding: 28 }}>
        {tab === "dashboard" && <DashboardView data={dashboard} />}
        {tab === "tenants" && <TenantsView tenants={tenants} onRefresh={() => { loadTenants(); loadDashboard(); }} />}
      </div>
    </div>
  );
}
